import React, { useState, useCallback } from 'react'
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Tooltip,
  Typography,
  styled,
} from '@mui/material'
import {
  CampaignSetupFormValues,
  DestinationType,
  BudgetType,
  BidStrategy,
  TargetingCity,
} from '../types'
import StepNavigation from './StepNavigation'
import axiosInstance from '../../../../../utils/axiosInstance'

const IS_PRODUCTION = process.env.REACT_APP_ENV === 'production'

const SectionCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '8px',
  boxShadow: 'none',
  marginBottom: theme.spacing(3),
}))

const SubSectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: '0.95rem',
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(3),
  color: theme.palette.secondary.dark,
}))

interface Props {
  values: CampaignSetupFormValues
  onChange: (field: keyof CampaignSetupFormValues, value: unknown) => void
  onNext: () => void
  onBack: () => void
  readOnly?: boolean
}

const DESTINATION_TYPES: { value: DestinationType; label: string }[] = [
  { value: 'ON_AD', label: 'On Ad Instant Form (ON_AD)' },
  { value: 'WEBSITE', label: 'Website (WEBSITE)' },
]

const BUDGET_TYPES: { value: BudgetType; label: string }[] = [
  { value: 'daily_budget', label: 'Daily Budget' },
  { value: 'lifetime_budget', label: 'Lifetime Budget' },
]

const BID_STRATEGIES: { value: BidStrategy; label: string }[] = [
  { value: 'LOWEST_COST_WITHOUT_CAP', label: 'Lowest Cost (No Cap)' },
  { value: 'COST_CAP', label: 'Cost Cap' },
  { value: 'LOWEST_COST_WITH_BID_CAP', label: 'Bid Cap' },
]

interface CityOption {
  key: string
  name: string
  region?: string
  countryCode?: string
}

const AdSetStep: React.FC<Props> = ({ values, onChange, onNext, onBack, readOnly }) => {
  const [cityOptions, setCityOptions] = useState<CityOption[]>([])
  const [citySearchLoading, setCitySearchLoading] = useState(false)
  const searchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleCitySearch = useCallback((query: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
    if (!query || query.trim().length < 2) {
      setCityOptions([])
      return
    }
    searchTimerRef.current = setTimeout(async () => {
      setCitySearchLoading(true)
      try {
        const res = await axiosInstance.get('/api/social-marketing/geo/search', {
          params: { q: query.trim() },
        })
        setCityOptions(res.data?.results || [])
      } catch {
        setCityOptions([])
      } finally {
        setCitySearchLoading(false)
      }
    }, 350)
  }, [])

  const handleCityAdd = (city: CityOption | null) => {
    if (!city) return
    if (values.targetingCities.some((c) => c.key === city.key)) return
    onChange('targetingCities', [
      ...values.targetingCities,
      {
        key: city.key,
        name: city.name,
        region: city.region,
        countryCode: city.countryCode,
        radius: 40,
      },
    ])
  }

  const handleCityRemove = (key: string) => {
    onChange(
      'targetingCities',
      values.targetingCities.filter((c) => c.key !== key)
    )
  }

  const handleRadiusChange = (key: string, radius: number) => {
    onChange(
      'targetingCities',
      values.targetingCities.map((c) => (c.key === key ? { ...c, radius } : c))
    )
  }

  return (
    <Box>
      <SectionCard>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
            Ad Set
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Ad Set Name *"
                value={values.adSetName}
                onChange={(e) => onChange('adSetName', e.target.value)}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Performance Goal</InputLabel>
                <Select value="LINK_CLICKS" label="Performance Goal" disabled>
                  <MenuItem value="LINK_CLICKS">Link Clicks (LINK_CLICKS)</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Locked to Link Clicks
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Tooltip
                title={
                  !IS_PRODUCTION
                    ? 'Status is locked to Paused in non-production environments to avoid Facebook costs.'
                    : ''
                }
                placement="top"
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value="PAUSED" label="Status" disabled>
                    <MenuItem value="PAUSED">Paused (PAUSED)</MenuItem>
                    {IS_PRODUCTION && <MenuItem value="ACTIVE">Active (ACTIVE)</MenuItem>}
                  </Select>
                </FormControl>
              </Tooltip>
              {!IS_PRODUCTION && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Locked to Paused (non-production)
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Destination Type</InputLabel>
                <Select
                  value={values.destinationType}
                  label="Destination Type"
                  onChange={(e) => onChange('destinationType', e.target.value)}
                >
                  {DESTINATION_TYPES.map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Start Time *"
                type="datetime-local"
                value={values.startTime || ''}
                onChange={(e) => onChange('startTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="End Time (default: 14 days)"
                type="datetime-local"
                value={values.endTime || ''}
                onChange={(e) => onChange('endTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Budget Type</InputLabel>
                <Select
                  value={values.budgetType}
                  label="Budget Type"
                  onChange={(e) => onChange('budgetType', e.target.value)}
                >
                  {BUDGET_TYPES.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Amount (€) *"
                type="number"
                value={values.amount}
                onChange={(e) =>
                  onChange('amount', e.target.value === '' ? '' : Number(e.target.value))
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
                disabled={readOnly}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" disabled={readOnly}>
                <InputLabel>Bid Strategy</InputLabel>
                <Select
                  value={values.bidStrategy}
                  label="Bid Strategy"
                  onChange={(e) => onChange('bidStrategy', e.target.value)}
                >
                  {BID_STRATEGIES.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Targeting section */}
          <SubSectionTitle>Targeting (Audience)</SubSectionTitle>

          <Grid container spacing={2}>
            {/* Country — locked to Germany */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                size="small"
                label="Country"
                value="Germany (DE)"
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>

            {/* City search autocomplete */}
            <Grid item xs={12} sm={8}>
              <Autocomplete<CityOption>
                options={cityOptions}
                getOptionLabel={(o) => (o.region ? `${o.name} (${o.region})` : o.name)}
                isOptionEqualToValue={(a, b) => a.key === b.key}
                loading={citySearchLoading}
                filterOptions={(x) => x}
                onInputChange={(_e, val) => handleCitySearch(val)}
                onChange={(_e, val) => handleCityAdd(val)}
                value={null}
                disabled={readOnly}
                blurOnSelect
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Search city"
                    placeholder="e.g. Berlin, Munich…"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {citySearchLoading && <CircularProgress size={16} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>

            {/* Selected cities with per-city radius */}
            {values.targetingCities.length > 0 && (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                  {values.targetingCities.map((city: TargetingCity) => (
                    <Box
                      key={city.key}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {city.name}
                          {city.region && (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ ml: 0.5 }}
                            >
                              {city.region}
                            </Typography>
                          )}
                        </Typography>
                        <Chip
                          label="Remove"
                          size="small"
                          variant="outlined"
                          onClick={() => handleCityRemove(city.key)}
                          disabled={readOnly}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
                          Radius
                        </Typography>
                        <Slider
                          size="small"
                          min={10}
                          max={80}
                          step={5}
                          value={city.radius}
                          onChange={(_e, val) => handleRadiusChange(city.key, val as number)}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `${v} km`}
                          sx={{ flex: 1 }}
                          disabled={readOnly}
                        />
                        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>
                          {city.radius} km
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}

            {values.targetingCities.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  No cities selected — campaign will target all of Germany (DE).
                </Typography>
              </Grid>
            )}

            <Grid item xs={6} sm={4}>
              <Tooltip
                title="Age targeting is fixed to 18-65 for employment campaigns."
                placement="top"
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Min Age"
                  type="number"
                  value={values.targetingAgeMin}
                  disabled
                  inputProps={{ min: 18, max: 65 }}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={6} sm={4}>
              <Tooltip
                title="Age targeting is fixed to 18-65 for employment campaigns."
                placement="top"
              >
                <TextField
                  fullWidth
                  size="small"
                  label="Max Age"
                  type="number"
                  value={values.targetingAgeMax}
                  disabled
                  inputProps={{ min: 18, max: 65 }}
                />
              </Tooltip>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Age range is fixed to 18–65 for employment ad campaigns.
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Tooltip
                title="Advantage+ placement is used automatically for optimal reach."
                placement="top"
              >
                <FormControl fullWidth size="small">
                  <InputLabel>Placements</InputLabel>
                  <Select value="ADVANTAGE_PLUS" label="Placements" disabled>
                    <MenuItem value="ADVANTAGE_PLUS">Advantage+ Placements (recommended)</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: 'block' }}
              >
                Fixed to Advantage+ for optimal performance
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </SectionCard>

      <StepNavigation onBack={onBack} onNext={onNext} />
    </Box>
  )
}

export default AdSetStep
